import React, { useEffect, useState, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Modal,
  Table,
  Pagination,
  Row,
  Col,
  Form,
  Offcanvas,
} from "react-bootstrap";
import { FaSortUp, FaSortDown, FaSort } from "react-icons/fa";
import NotyfContext from "../../../../contexts/NotyfContext";
import Loader from "../../../../components/spinners/Loader";
import { getSimOrders } from "../../../../redux/slices/orders";
import useWindowSize from "../../../../hooks/useWindowSize";
import { useWhitelabel } from "../../../../contexts/WhitelabelContext";
import {
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
  useFilters,
} from "react-table";
import useDeviceType from "../../../../hooks/useDeviceType";

const SimOrdersModal = ({
  imsi,
  showSimOrdersModal,
  setShowSimOrdersModal,
}) => {
  const { backgroundColor, fontColor } = useWhitelabel();
  const { t } = useTranslation();
  const notyf = useContext(NotyfContext);
  const { user } = useSelector((state) => state.user);
  const { impersonationUser } = useSelector((state) => state.impersonationUser);
  const [activeUser, setActiveUser] = useState({});
  const orders = useSelector((state) => state.orders.orders);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [internalShow, setInternalShow] = useState(showSimOrdersModal);
  const [fadeOut, setFadeOut] = useState(false);
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";

  const orderColumns = useMemo(() => {
    return [
      {
        Header: "ID",
        accessor: "orderId",
      },
      {
        Header: "Name",
        accessor: "name",
      },
      {
        Header: "Status",
        accessor: "status",
      },
      {
        Header: "Creator",
        accessor: "createdBy",
      },
      {
        Header: "Creation Date",
        accessor: "createdAt",
      },
      {
        Header: "Last Update",
        accessor: "updatedAt",
      },
    ];
  }, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    prepareRow,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns: orderColumns,
      data: orders,
      initialState: { pageIndex: 0 },
    },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect
  );

  useEffect(() => {
    setActiveUser(impersonationUser.id ? impersonationUser : user);
  }, [impersonationUser, user]);

  useEffect(() => {
    if (showSimOrdersModal && !isDesktop) {
      setFadeOut(false);
      setInternalShow(true);
    }
  }, [showSimOrdersModal, isDesktop]);

  const handleHide = () => {
    setFadeOut(true);
    setTimeout(() => {
      setInternalShow(false);
      setShowSimOrdersModal(false);
    }, 300);
  };

  useEffect(() => {
    const fetchSimOrders = async () => {
      await dispatch(getSimOrders({ imsi }))
        .catch((error) => {
          notyf.open({
            type: "danger",
            message: error,
            duration: 5000,
            ripple: true,
            dismissible: false,
            position: {
              x: "center",
              y: "top",
            },
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    if (activeUser.id && imsi && showSimOrdersModal) {
      fetchSimOrders();
    }
  }, [activeUser, imsi, showSimOrdersModal, dispatch, notyf]);

  if (isDesktop) {
    return (
      <Modal
        show={showSimOrdersModal}
        onHide={() => setShowSimOrdersModal(false)}
        centered
        size="lg"
        backdrop="static"
        scrollable
      >
        <Modal.Header>
          <Modal.Title>SIM Orders: {imsi}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoading && <Loader />}
          {!isLoading && orders.length === 0 && (
            <div className="d-flex justify-content-center align-items-center w-100 h-100">
              <p>No SIM Orders</p>
            </div>
          )}
          {!isLoading && orders.length > 0 && (
            <React.Fragment>
              <Table id="emailsTable" hover responsive {...getTableProps()}>
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th {...column.getHeaderProps()}>
                          <span {...column.getSortByToggleProps()}>
                            {column.render("Header")}
                            <span>
                              {column.sortable ? (
                                column.isSorted ? (
                                  column.isSortedDesc ? (
                                    <FaSortUp className="ms-lg-2 ms-xl-2" />
                                  ) : (
                                    <FaSortDown className="ms-lg-2 ms-xl-2" />
                                  )
                                ) : (
                                  <FaSort className="ms-lg-2 ms-xl-2" />
                                )
                              ) : null}
                            </span>
                          </span>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map((row, i) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => {
                          return (
                            <td {...cell.getCellProps()}>
                              {cell.render("Cell")}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <Row>
                <Col md="8">
                  <span className="mx-2">
                    {t("table.page")}{" "}
                    <strong>
                      {pageIndex + 1} {t("table.of")} {pageOptions.length}
                    </strong>
                  </span>
                  <span className="ms-3 me-2">{t("table.show")}:</span>
                  <Form.Select
                    className="d-inline-block w-auto"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                    }}
                  >
                    {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize}
                      </option>
                    ))}
                  </Form.Select>

                  <span className="ms-3 me-2">{t("table.goToPage")}:</span>
                  <Form.Control
                    className="d-inline-block"
                    type="number"
                    defaultValue={pageIndex + 1}
                    onChange={(e) => {
                      const page = e.target.value
                        ? Number(e.target.value) - 1
                        : 0;
                      gotoPage(page);
                    }}
                    style={{ width: "75px" }}
                  />
                </Col>
                <Col md="4">
                  <Pagination className="float-end">
                    <Pagination.First
                      onClick={() => {
                        gotoPage(0);
                      }}
                      disabled={!canPreviousPage}
                    />
                    <Pagination.Prev
                      onClick={() => {
                        previousPage();
                      }}
                      disabled={!canPreviousPage}
                    />
                    <Pagination.Next
                      onClick={() => {
                        nextPage();
                      }}
                      disabled={!canNextPage}
                    />
                    <Pagination.Last
                      onClick={() => {
                        gotoPage(pageCount - 1);
                      }}
                      disabled={!canNextPage}
                    />
                  </Pagination>
                </Col>
              </Row>
            </React.Fragment>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            style={{
              "--dynamic-bg-color": backgroundColor,
              "--dynamic-font-color": fontColor,
              border: "none",
            }}
            onClick={() => setShowSimOrdersModal(false)}
          >
            {t("simProfile.orders.close")}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  } else {
    return (
      <Offcanvas
        show={internalShow}
        placement="bottom"
        scrollable="true"
        onHide={handleHide}
        className={`fullscreen-offcanvas ${fadeOut ? "fade-out" : ""}`}
      >
        <Offcanvas.Header closeButton className="fullscreen-offcanvas-header">
          <Offcanvas.Title>SIM Orders: {imsi}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {isLoading && <Loader />}
          {!isLoading && orders.length === 0 && (
            <div className="d-flex justify-content-center align-items-center w-100 h-100">
              <p>No SIM Orders</p>
            </div>
          )}
          {!isLoading && orders.length > 0 && (
            <React.Fragment>
              <Table id="emailsTable" hover responsive {...getTableProps()}>
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th {...column.getHeaderProps()}>
                          <span {...column.getSortByToggleProps()}>
                            {column.render("Header")}
                            <span>
                              {column.sortable ? (
                                column.isSorted ? (
                                  column.isSortedDesc ? (
                                    <FaSortUp className="ms-lg-2 ms-xl-2" />
                                  ) : (
                                    <FaSortDown className="ms-lg-2 ms-xl-2" />
                                  )
                                ) : (
                                  <FaSort className="ms-lg-2 ms-xl-2" />
                                )
                              ) : null}
                            </span>
                          </span>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map((row, i) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => {
                          return (
                            <td {...cell.getCellProps()}>
                              {cell.render("Cell")}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <div className="emails-pagination mt-3">
                <Row className="mb-3">
                  <Col className="d-flex justify-content-center">
                    <Pagination className="float-end">
                      <Pagination.Prev
                        onClick={() => previousPage()}
                        disabled={!canPreviousPage}
                      />
                      <Col className="d-flex align-items-end mx-2 px-3">
                        <span className="mx-2">
                          <strong>
                            {pageIndex + 1} {t("table.of")} {pageOptions.length}
                          </strong>
                        </span>
                      </Col>
                      <Pagination.Next
                        onClick={() => nextPage()}
                        disabled={!canNextPage}
                      />
                    </Pagination>
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col xs="auto">
                    <span className="me-2">{t("table.show")}:</span>
                    <Form.Select
                      className="d-inline-block w-auto"
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                      }}
                      style={{
                        padding: "0.25rem 2.1rem 0.25rem 0.7rem",
                        border: "none",
                        boxShadow: "none",
                        fontSize: "14px",
                      }}
                    >
                      {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                        <option key={pageSize} value={pageSize}>
                          {pageSize}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col>
                    <div className="float-end">
                      <span className="me-2">{t("table.goToPage")}:</span>
                      <Form.Control
                        className="d-inline-block w-auto"
                        type="number"
                        defaultValue={pageIndex + 1}
                        min={1}
                        max={pageCount}
                        onChange={(e) => {
                          const page = e.target.value
                            ? Number(e.target.value) - 1
                            : 0;
                          gotoPage(page);
                        }}
                        style={{
                          padding: "0.25rem 1.2rem 0.25rem 0.7rem",
                          fontSize: "0.825rem",
                        }}
                      />
                    </div>
                  </Col>
                </Row>
              </div>
            </React.Fragment>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    );
  }
};

export default SimOrdersModal;
